import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * DAVIDsTEA recently viewed band.
 *
 * The product ids live in localStorage under `viewedProducts`, which Horizon already writes
 * on every product page and already reads in the search drawer. Nothing new is stored here.
 *
 * The cards themselves are rendered server-side: the component asks the Section Rendering API
 * for this same section through the search route, so the markup that comes back is the
 * _product-card theme block with the collection grid's own blocks - badge, overline, title,
 * rating, price, quick add. Building the cards in JavaScript would have meant maintaining a
 * second copy of that design.
 *
 * @extends {Component<{}>}
 */
export class DtRecentlyViewed extends Component {
  requiredRefs = ['grid'];

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  async #load() {
    let ids = [];

    try {
      ids = RecentlyViewed.getProducts();
    } catch (e) {
      // Private browsing or a blocked storage: the band simply stays hidden.
      return;
    }

    if (!ids.length) return;

    const limit = Number(this.dataset.limit) || ids.length;
    ids = ids.slice(0, limit);

    // The search route is what makes `search.results` available to the section. `type=product`
    // keeps pages and articles out of the results, since an id match can hit those too.
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    let html;
    try {
      html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
    } catch (e) {
      return;
    }

    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');
    if (!incoming || !incoming.children.length) return;

    // Search returns its own ranking, so the cards come back in an order that has nothing to do
    // with when they were viewed. Put them back in the order the ids were stored in.
    const cards = [...incoming.children];
    cards.sort((a, b) => {
      const ia = ids.indexOf(a.querySelector('[data-product-id]')?.dataset.productId ?? '');
      const ib = ids.indexOf(b.querySelector('[data-product-id]')?.dataset.productId ?? '');
      return (ia === -1 ? cards.length : ia) - (ib === -1 ? cards.length : ib);
    });

    this.refs.grid.replaceChildren(...cards);
    this.removeAttribute('hidden');
  }
}

if (!customElements.get('dt-recently-viewed-component')) {
  customElements.define('dt-recently-viewed-component', DtRecentlyViewed);
}
